import type { Player } from '../../types/player';

type Pos = 'QB' | 'RB' | 'WR' | 'TE' | 'K' | 'DEF';

// Market targets for Top-24 in standard 1QB leagues (unused for now)
// const MARKET_TARGET: Record<Pos, number> = { 
//   QB: 1, 
//   RB: 10, 
//   WR: 9, 
//   TE: 3, 
//   K: 1, 
//   DEF: 0 // We'll handle DEF separately if needed
// };

// const FLEX_TOLERANCE = 1; // allow ±1 flex vs targets (unused for now)

// Baselines for VORP calculation
const BASELINE_RANK: Record<Pos, number> = { 
  QB: 12,  // QB12 baseline in 1QB
  RB: 24,  // RB24 baseline
  WR: 30,  // WR30 baseline  
  TE: 12,  // TE12 baseline
  K: 12,   // K12 baseline
  DEF: 12  // DEF12 baseline
};

// Helper functions
function byProjDesc(a: any, b: any) { 
  return (b.projection || 0) - (a.projection || 0); 
}

function pickBaseline(players: Player[], pos: Pos, rank: number): number {
  const pool = players.filter(p => p.position === pos).sort(byProjDesc);
  const idx = Math.min(rank - 1, Math.max(0, pool.length - 1));
  return pool[idx]?.projection || 0;
}

/**
 * Calculate VORP for all players with aggressive position scarcity multipliers
 */
function calculateVORP(players: Player[]): Player[] {
  const baselines: Record<Pos, number> = {
    QB: pickBaseline(players, 'QB', BASELINE_RANK.QB),
    RB: pickBaseline(players, 'RB', BASELINE_RANK.RB),
    WR: pickBaseline(players, 'WR', BASELINE_RANK.WR),
    TE: pickBaseline(players, 'TE', BASELINE_RANK.TE),
    K: pickBaseline(players, 'K', BASELINE_RANK.K),
    DEF: pickBaseline(players, 'DEF', BASELINE_RANK.DEF),
  };

  // Aggressive scarcity multipliers to fix market alignment
  const scarcityMultipliers: Record<Pos, number> = {
    QB: 0.15,  // Massive penalty for QBs in 1QB format
    RB: 3.0,   // Huge boost for RB scarcity
    WR: 1.0,   // Baseline
    TE: 2.5,   // Large boost for TE scarcity
    K: 0.8,    // Slight penalty
    DEF: 0.6   // Penalty
  };

  return players.map(p => {
    const rawVORP = Math.max(0, (p.projection || 0) - (baselines[p.position as Pos] || 0));
    const multiplier = scarcityMultipliers[p.position as Pos] || 1.0;
    
    return {
      ...p,
      vorp: rawVORP * multiplier,
    };
  });
}

/**
 * Guards to keep noise out of the Top-24
 */
function passesSanity(p: Player): boolean {
  // Filter out retired/inactive players from top consideration
  if ('isActive' in p && (p as any).isActive === false) return false;
  
  // Filter out players with very low projections (likely inactive/retired)
  const minProjections: Record<Pos, number> = {
    QB: 180,  // ~11 pts/game minimum for QBs
    RB: 80,   // ~5 pts/game minimum for RBs
    WR: 100,  // ~6 pts/game minimum for WRs
    TE: 70,   // ~4 pts/game minimum for TEs
    K: 80,    // ~5 pts/game minimum for Ks
    DEF: 80   // ~5 pts/game minimum for DEFs
  };
  
  const minProjection = minProjections[p.position as Pos] || 0;
  if ((p.projection || 0) < minProjection) return false;
  
  // Depth chart gate: RB3/WR5+ need exceptional value to leapfrog
  const depthRank = (p as any).depthRank || 1;
  if ((p.position === 'RB' && depthRank >= 3) ||
      (p.position === 'WR' && depthRank >= 5)) {
    return (p.vorp || 0) >= 50; // Much higher bar for backups
  }
  
  // Extra QB filtering - only allow truly elite QBs
  if (p.position === 'QB' && (p.projection || 0) < 300) {
    return false; // Only QBs with 300+ projections (~18.75 ppg)
  }
  
  return true;
}

/**
 * Apply age volatility penalty and additional position adjustments
 */
function ageVolatilityPenalty(p: Player): number {
  let adjustedValue = p.vorp || 0;
  const age = p.age || 27;
  
  // Age penalties
  if (p.position === 'RB' && age > 29) adjustedValue -= 5;
  if (p.position === 'WR' && age > 31) adjustedValue -= 3;
  if (p.position === 'QB' && age > 35) adjustedValue -= 2;
  
  // Additional QB penalty to ensure only elite QBs survive
  if (p.position === 'QB') {
    adjustedValue *= 0.5; // Cut QB values in half again
  }
  
  // Boost for proven bellcow RBs (high touch share indicators)
  if (p.position === 'RB' && (p.projection || 0) > 200) {
    adjustedValue *= 1.3; // 30% boost for projected bellcows
  }
  
  // Boost for elite TEs
  if (p.position === 'TE' && (p.projection || 0) > 150) {
    adjustedValue *= 1.4; // 40% boost for elite TEs
  }
  
  return Math.max(0, adjustedValue);
}

/**
 * Main finalizer function that enforces market alignment
 */
export function finalizeRanks(rawPlayers: Player[]) {
  const withVorp = calculateVORP(rawPlayers);
  const pool = withVorp.map(p => {
    const adjustedValue = ageVolatilityPenalty(p);
    return { 
      ...p, 
      value: adjustedValue // Use value property instead of adj
    };
  });

  // Step 1: enforce positional targets inside Top-24 (strict enforcement)
  // const targets: Record<Pos, number> = { ...MARKET_TARGET }; // unused for now
  const hardMin: Record<Pos, number> = { 
    QB: 1,  // Exactly 1 QB
    RB: 10, // Exactly 10 RBs
    WR: 9,  // Exactly 9 WRs
    TE: 3,  // Exactly 3 TEs
    K: 1,   // Exactly 1 K
    DEF: 0
  };

  // Pre-pick best per position to satisfy exact targets
  const picked: Player[] = [];
  
  // Force exactly the right distribution
  const positionOrder: Pos[] = ['QB', 'RB', 'WR', 'TE', 'K']; // Process in priority order
  
  positionOrder.forEach(pos => {
    const target = hardMin[pos];
    if (target > 0) {
      const eligible = pool
        .filter(p => p.position === pos && passesSanity(p))
        .sort((a, b) => (b.value || 0) - (a.value || 0));
      
      // Take exactly the target number
      const take = Math.min(target, eligible.length);
      picked.push(...eligible.slice(0, take));
      
      console.log(`Market Alignment: Selected ${take} ${pos}s for Top-24`);
    }
  });

  // Strict caps - no tolerance for QB overflow
  const POS_CAP: Record<Pos, number> = {
    QB: 1,  // Hard cap at 1 QB
    RB: 10, // Hard cap at 10 RBs
    WR: 9,  // Hard cap at 9 WRs
    TE: 3,  // Hard cap at 3 TEs
    K: 1,   // Hard cap at 1 K
    DEF: 0
  };

  // Count what we have so far
  const counts: Record<Pos, number> = { QB: 0, RB: 0, WR: 0, TE: 0, K: 0, DEF: 0 };
  picked.forEach(p => counts[p.position as Pos]++);

  console.log('Market Alignment: Current Top-24 distribution:', counts);
  
  // If we're short of 24, fill remaining slots but maintain strict position caps
  const pickedIds = new Set(picked.map(p => p.id));
  
  if (picked.length < 24) {
    const remaining = pool
      .filter(p => !pickedIds.has(p.id) && passesSanity(p))
      .sort((a, b) => (b.value || 0) - (a.value || 0));

    for (const p of remaining) {
      const pos = p.position as Pos;
      if (picked.length >= 24) break;
      
      // Only add if we haven't hit the strict cap for this position
      if (counts[pos] < POS_CAP[pos]) {
        picked.push(p);
        counts[pos]++;
      }
    }
  }

  // Fill to 24 if we fell short (e.g., due to sanity filters) with best remaining value regardless of caps
  if (picked.length < 24) {
    const filler = pool
      .filter(p => !pickedIds.has(p.id) && !picked.some(x => x.id === p.id))
      .sort((a, b) => (b.value || 0) - (a.value || 0));
    for (const p of filler) {
      if (picked.length >= 24) break;
      picked.push(p);
    }
  }

  // Rank everything by value for the full board, push retired/inactive beyond 300
  const full = pool.map(p => {
    if ('isActive' in p && (p as any).isActive === false) {
      return { ...p, value: -1e9 };
    }
    return p;
  }).sort((a, b) => (b.value || 0) - (a.value || 0));

  const top24 = picked.sort((a, b) => (b.value || 0) - (a.value || 0)).slice(0, 24);

  // For UI metrics
  const top24Counts = { QB: 0, RB: 0, WR: 0, TE: 0, K: 0, DEF: 0 } as Record<Pos, number>;
  top24.forEach(p => (top24Counts[p.position as Pos]++));

  // Assign ranks
  const rankedPlayers = full.map((player, index) => ({
    ...player,
    rank: index + 1
  }));

  return { 
    full: rankedPlayers, 
    top24, 
    top24Counts 
  };
}

/**
 * Legacy function for backward compatibility
 */
export function applyMarketAlignment(players: Player[]): Player[] {
  const result = finalizeRanks(players);
  return result.full;
}



